import { toWSDateString, WSPillProps } from "@wingspanhq/fe-component-library";
import { Payable } from "../../../../services/api/payments/payables";

// Result shape consumed by WSTableCell's `pill` + `secondaryText` props.
export type PayableStatusCellProps = {
  pill: WSPillProps;
  secondaryText?: string;
};

// Pill presets — mirror the legacy `mapStatusProjection`, but driven by the
// new 7-value Payable.status enum + payer/payee workflow refinements.
const PILL_DRAFT: WSPillProps = {
  theme: "neutral",
  icon: "draft",
  text: "Draft"
};
const PILL_CANCELLED: WSPillProps = {
  theme: "neutral",
  icon: "draft",
  text: "Cancelled"
};
const PILL_ACTION_REQUIRED: WSPillProps = {
  theme: "warning",
  icon: true,
  text: "Action required"
};
const PILL_AWAITING_CONTRACTOR: WSPillProps = {
  theme: "neutral",
  icon: "time",
  text: "Awaiting contractor"
};
const PILL_SCHEDULED: WSPillProps = {
  theme: "blue",
  icon: "calendar",
  text: "Scheduled"
};
const PILL_PAID: WSPillProps = {
  theme: "success",
  icon: true,
  text: "Paid"
};
const PILL_OFF_PLATFORM: WSPillProps = {
  theme: "neutral",
  icon: "paperclip",
  text: "Off-platform"
};
const PILL_IN_TRANSIT: WSPillProps = {
  theme: "blue",
  text: "In transit"
};
const PILL_UNKNOWN: WSPillProps = {
  theme: "neutral",
  icon: "draft",
  text: "Unknown"
};

// Mirrors legacy ClientWorkFlowStatus values that drive secondary-text branches.
const PAYER_APPROVED = "Approved";
const PAYER_PAYMENT_INITIATED = "PaymentInitiated";
const PAYER_FUNDED = "Funded";

// Mirrors legacy MemberWorkFlowStatus values.
const PAYEE_DISPUTED = "Disputed";

const formatMonthDate = (value: Date | undefined): string =>
  toWSDateString(value, "monthDate");

const isPayableDisputed = (payable: Payable): boolean => {
  if (payable.payee?.workflowStatus === PAYEE_DISPUTED) {
    return true;
  }
  // Fall back to the timestamp signal: disputed event recorded but never
  // resolved.
  const disputedAt =
    payable.events?.payeeDisputedAt ?? payable.events?.disputedAt;
  const resolvedAt = payable.events?.payerResolvedDisputeAt;
  return Boolean(disputedAt) && !resolvedAt;
};

const isPayableResubmitted = (payable: Payable): boolean =>
  Boolean(payable.events?.payeeResubmittedAt);

export const getPayableStatusCellProps = (
  payable: Payable
): PayableStatusCellProps => {
  const status = payable.status;
  const events = payable.events;
  const payerWorkflowStatus = payable.payer?.workflowStatus;

  if (status === "Draft") {
    // Legacy uses `createdAt` here. The new Payable schema does not expose a
    // top-level `createdAt`; `events.openedAt` is the closest equivalent for
    // "when the draft entered the system".
    return {
      pill: PILL_DRAFT,
      secondaryText: events?.openedAt
        ? `Created • ${formatMonthDate(events.openedAt)}`
        : undefined
    };
  }

  if (status === "Cancelled") {
    return { pill: PILL_CANCELLED };
  }

  if (status === "Paid") {
    if (events?.markedPaidAt) {
      return {
        pill: PILL_OFF_PLATFORM,
        secondaryText: "Historical records"
      };
    }
    return {
      pill: PILL_PAID,
      secondaryText: events?.paidAt
        ? `Payout cleared • ${formatMonthDate(events.paidAt)}`
        : undefined
    };
  }

  if (status === "PaymentInTransit") {
    return {
      pill: PILL_IN_TRANSIT,
      secondaryText: "Payment in route"
    };
  }

  if (isPayableDisputed(payable)) {
    return {
      pill: PILL_ACTION_REQUIRED,
      secondaryText: events?.payeeDisputedAt
        ? `Disputed by contractor • ${formatMonthDate(events.payeeDisputedAt)}`
        : "Disputed by contractor"
    };
  }

  if (isPayableResubmitted(payable)) {
    return {
      pill: PILL_ACTION_REQUIRED,
      secondaryText: `Contractor resubmitted • ${formatMonthDate(
        events?.payeeResubmittedAt
      )}`
    };
  }

  if (status === "Open" || status === "Overdue") {
    if (
      payerWorkflowStatus === PAYER_APPROVED ||
      payerWorkflowStatus === PAYER_PAYMENT_INITIATED
    ) {
      // Legacy resolves an exact payroll cut-off here via `getMatchingPayroll`.
      // The new module doesn't thread payroll settings into the table cell, so
      // we fall back to the generic future-payroll label.
      return {
        pill: PILL_SCHEDULED,
        secondaryText: "Future payroll"
      };
    }

    return {
      pill: PILL_ACTION_REQUIRED,
      secondaryText: "Approval by you"
    };
  }

  if (status === "Pending") {
    if (
      payerWorkflowStatus === PAYER_PAYMENT_INITIATED ||
      payerWorkflowStatus === PAYER_FUNDED
    ) {
      return {
        pill: PILL_PAID,
        secondaryText: "Funded for payment"
      };
    }

    if (payerWorkflowStatus === PAYER_APPROVED) {
      return {
        pill: PILL_SCHEDULED,
        secondaryText: "To be funded • Future payroll"
      };
    }

    return {
      pill: PILL_AWAITING_CONTRACTOR,
      secondaryText: "Eligibility pending"
    };
  }

  return { pill: PILL_UNKNOWN };
};
